import React, { useState } from "react";
import { Printer, Copy, Check, Download, ShieldCheck, Lock, Unlock, Calendar } from "lucide-react";
import {
  Opportunity,
  Requirement,
  ProposalMilestone,
  ProposalAddendum,
  RequiredForm,
  FormatRule,
  ProposalSectionGroup,
  StrategicAssessment,
  PlanReadinessReport,
  UserProfile,
} from "../../types";
import { generateExecutiveBriefHtml } from "../../services/planningExportService";
import { formatCountdown } from "../../utils/calendarUtils";

interface ExecutiveBriefViewProps {
  opportunity: Opportunity;
  assessment?: StrategicAssessment | null;
  requirements: Requirement[];
  milestones: ProposalMilestone[];
  addenda: ProposalAddendum[];
  forms: RequiredForm[];
  formatRules: FormatRule[];
  sectionGroups: ProposalSectionGroup[];
  readinessReport: PlanReadinessReport;
  user?: UserProfile | null;
  onOpenReadinessModal: () => void;
  onOpenExportModal: () => void;
}

export const ExecutiveBriefView: React.FC<ExecutiveBriefViewProps> = ({
  opportunity,
  assessment,
  requirements,
  milestones,
  addenda,
  forms,
  formatRules,
  sectionGroups,
  readinessReport,
  user: _user,
  onOpenReadinessModal,
  onOpenExportModal,
}) => {
  const [copied, setCopied] = useState(false);

  const mandatoryCount = requirements.filter((r) => r.mandatory).length;
  const confirmedMandatory = requirements.filter((r) => r.mandatory && r.confirmed).length;
  const pendingForms = forms.filter(
    (f) => f.mandatory && f.status !== "attached" && f.status !== "signed",
  ).length;
  const pendingAddenda = addenda.filter(
    (a) => a.acknowledgmentRequired && a.acknowledgmentStatus !== "acknowledged",
  ).length;

  const brief = assessment?.executiveBrief;
  const isFrozen = Boolean(opportunity.planFrozen);

  const countdown = opportunity.dueDate
    ? formatCountdown(opportunity.dueDate, opportunity.dueTime)
    : null;

  const handlePrint = () => {
    window.print();
  };

  const handleCopyHtml = async () => {
    try {
      const html = generateExecutiveBriefHtml({
        opportunity,
        assessment,
        requirements,
        milestones,
        addenda,
        forms,
        formatRules,
        sectionGroups,
      });

      if (navigator.clipboard && window.ClipboardItem) {
        const blobHtml = new Blob([html], { type: "text/html" });
        const blobPlain = new Blob([`Executive Brief for ${opportunity.title}`], {
          type: "text/plain",
        });
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": blobHtml,
            "text/plain": blobPlain,
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(html);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy brief:", err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Action & Status Bar (Hidden in Print) */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm print:hidden dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-3">
          {isFrozen ? (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
              <Lock className="h-4 w-4" />
              <span>
                Plan Frozen Baseline (
                {opportunity.planFrozenAt
                  ? new Date(opportunity.planFrozenAt).toLocaleDateString()
                  : "Locked"}
                )
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              <Unlock className="h-4 w-4 text-amber-500" />
              <span>Plan In Progress</span>
            </div>
          )}

          <button
            type="button"
            onClick={onOpenReadinessModal}
            className="btn-animate flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <ShieldCheck className="h-4 w-4 text-[#2ba5b5]" />
            <span>Readiness Gate: {readinessReport.overallScore}%</span>
            {readinessReport.blockersCount > 0 && (
              <span className="ml-1 rounded-full bg-rose-500 px-1.5 py-0.2 text-[10px] text-white">
                {readinessReport.blockersCount} Blockers
              </span>
            )}
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopyHtml}
            className="btn-animate flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title="Copy formatted brief for email or Google Docs"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-emerald-600">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                <span>Copy Brief</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="btn-animate flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <Printer className="h-3.5 w-3.5" />
            <span>Print / PDF</span>
          </button>

          <button
            type="button"
            onClick={onOpenExportModal}
            className="btn-animate flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#238a97]"
          >
            <Download className="h-3.5 w-3.5" />
            <span>Export Planning Package</span>
          </button>
        </div>
      </div>

      {/* Main Printable Document Card */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 print:border-none print:shadow-none print:p-0">
        {/* Document Header */}
        <div className="border-b-2 border-[#2ba5b5] pb-4 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="inline-block rounded bg-[#2ba5b5]/10 px-2 py-0.5 text-[10px] font-bold tracking-wide text-[#2ba5b5] uppercase">
                  MLC Proposal Response Plan
                </span>
                {isFrozen && (
                  <span className="inline-block rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                    FROZEN BASELINE
                  </span>
                )}
              </div>
              <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                {opportunity.title}
              </h1>
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Client:{" "}
                <strong className="text-slate-800 dark:text-slate-200">{opportunity.client}</strong>{" "}
                &bull; Proposal Lead:{" "}
                <strong className="text-slate-800 dark:text-slate-200">{opportunity.owner}</strong>{" "}
                {opportunity.ownerEmail ? `(${opportunity.ownerEmail})` : ""}
              </div>
            </div>

            <div className="text-right">
              <div className="text-sm font-bold text-[#2ba5b5]">MULTILINGUAL CONNECTIONS</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                Certified Women&apos;s Business Enterprise (WBE)
              </div>
              {countdown && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  <Calendar className="h-3 w-3 text-[#2ba5b5]" />
                  <span>{countdown.label}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Executive Meta Strip */}
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3.5 sm:grid-cols-4 dark:border-slate-800 dark:bg-slate-900/60">
          <div>
            <div className="text-[10px] font-semibold text-slate-400 uppercase">
              Submission Due Date
            </div>
            <div className="text-xs font-bold text-slate-900 dark:text-white">
              {opportunity.dueDate || "Not specified"}
              {opportunity.dueTime ? ` @ ${opportunity.dueTime}` : ""}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-slate-400 uppercase">Current Stage</div>
            <div className="text-xs font-bold text-slate-900 capitalize dark:text-white">
              {opportunity.stage.replace(/_/g, " ")}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-slate-400 uppercase">Core Services</div>
            <div className="text-xs font-bold text-slate-900 dark:text-white truncate">
              {opportunity.services && opportunity.services.length > 0
                ? opportunity.services.join(", ")
                : "Translation & Localization"}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-slate-400 uppercase">
              Qualification Decision
            </div>
            <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
              {opportunity.decision === "go"
                ? "APPROVED GO"
                : opportunity.decision?.toUpperCase() || "UNDECIDED"}
            </div>
          </div>
        </div>

        {/* Section 1: Win Strategy & Themes */}
        <div className="mt-6 space-y-3">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-200 pb-1.5 dark:text-white dark:border-slate-800">
            1. Strategic Fit & Win Strategy
          </h2>

          <div className="rounded-lg border-l-4 border-[#2ba5b5] bg-teal-50/50 p-3.5 dark:bg-teal-950/20">
            <div className="text-xs font-bold text-teal-900 dark:text-teal-200">
              Primary Win Theme:{" "}
              {brief?.recommendedWinTheme ||
                "Tailored Multilingual Excellence with Certified WBE Agility"}
            </div>
            <p className="mt-1 text-xs text-slate-700 dark:text-slate-300">
              {brief?.winStrategySummary ||
                "Emphasize responsive account management, vetted linguist networks, proven public sector compliance, and ISO-certified quality assurance workflows."}
            </p>
          </div>

          {brief?.keyDifferentiators && brief.keyDifferentiators.length > 0 && (
            <div className="pt-1">
              <h3 className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Key MLC Differentiators:
              </h3>
              <ul className="mt-1 list-disc pl-5 text-xs text-slate-600 space-y-0.5 dark:text-slate-400">
                {brief.keyDifferentiators.map((diff, idx) => (
                  <li key={idx}>{diff}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Section 2: Bid Section Ownership Outline */}
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-200 pb-1.5 dark:border-slate-800">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider dark:text-white">
              2. Bid Section Ownership & Outline ({sectionGroups.length} Work Packages)
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    Ref
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    Section Title
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    Category
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    Primary Drafter
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    SME Reviewer
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300 text-right">
                    Target Words
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {sectionGroups.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-4 text-center text-xs text-slate-400 italic">
                      No bid sections structured yet. Auto-initialize in Section Ownership.
                    </td>
                  </tr>
                ) : (
                  sectionGroups.map((sec) => (
                    <tr key={sec.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                      <td className="py-2 px-3 font-bold text-slate-700 dark:text-slate-300">
                        {sec.sectionRef}
                      </td>
                      <td className="py-2 px-3 font-medium text-slate-900 dark:text-white">
                        {sec.title}
                      </td>
                      <td className="py-2 px-3 text-slate-500 capitalize">{sec.category}</td>
                      <td className="py-2 px-3">
                        {sec.assignedTo ? (
                          <span className="font-semibold text-slate-800 dark:text-slate-200">
                            {sec.assignedTo}
                          </span>
                        ) : (
                          <span className="font-semibold text-amber-600 dark:text-amber-400">
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-slate-600 dark:text-slate-400">
                        {sec.reviewerName || "Unassigned"}
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-slate-600 dark:text-slate-400">
                        {sec.targetWordCount > 0
                          ? `${sec.targetWordCount.toLocaleString()} words`
                          : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 3: Response Schedule & Gate Milestones */}
        <div className="mt-6 space-y-3">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-200 pb-1.5 dark:text-white dark:border-slate-800">
            3. Response Schedule Milestones ({milestones.length} Gates)
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    Milestone
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    Category
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    Target Due Date
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300">
                    Owner
                  </th>
                  <th className="py-2 px-3 font-semibold text-slate-600 dark:text-slate-300 text-right">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {milestones.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-xs text-slate-400 italic">
                      No response milestones scheduled.
                    </td>
                  </tr>
                ) : (
                  milestones.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                      <td className="py-2 px-3 font-medium text-slate-900 dark:text-white">
                        {m.title}
                      </td>
                      <td className="py-2 px-3 text-slate-500 capitalize">
                        {m.category.replace(/_/g, " ")}
                      </td>
                      <td className="py-2 px-3 font-semibold text-slate-800 dark:text-slate-200">
                        {m.targetDate}
                        {m.targetTime ? ` ${m.targetTime}` : ""}
                      </td>
                      <td className="py-2 px-3 text-slate-600 dark:text-slate-400">
                        {m.ownerName || m.ownerEmail || "Proposal Lead"}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                            m.status === "completed"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          {m.status.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 4: Compliance Scorecard */}
        <div className="mt-6 space-y-3">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-200 pb-1.5 dark:text-white dark:border-slate-800">
            4. Compliance & Submission Scorecard
          </h2>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center dark:border-slate-800 dark:bg-slate-900/60">
              <div className="text-xl font-bold text-slate-900 dark:text-white">
                {confirmedMandatory} / {mandatoryCount}
              </div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase mt-0.5">
                Mandatory Reqs Confirmed
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center dark:border-slate-800 dark:bg-slate-900/60">
              <div
                className={`text-xl font-bold ${
                  pendingAddenda === 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-amber-600 dark:text-amber-400"
                }`}
              >
                {addenda.length - pendingAddenda} / {addenda.length}
              </div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase mt-0.5">
                Addenda Acknowledged
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center dark:border-slate-800 dark:bg-slate-900/60">
              <div
                className={`text-xl font-bold ${
                  pendingForms === 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-amber-600 dark:text-amber-400"
                }`}
              >
                {forms.length - pendingForms} / {forms.length}
              </div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase mt-0.5">
                Mandatory Forms Ready
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center dark:border-slate-800 dark:bg-slate-900/60">
              <div className="text-xl font-bold text-slate-900 dark:text-white">
                {formatRules.length}
              </div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase mt-0.5">
                Format Rules Enforced
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 border-t border-slate-200 pt-3 text-[10px] text-slate-400 flex justify-between dark:border-slate-800">
          <span>MLC Proposal HQ &bull; Confidential Proposal Planning Record</span>
          <span>Printed / Generated: {new Date().toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};
